"use client";

import { c as _c } from "react/compiler-runtime";
import { CalendarDays } from "lucide-react";
import { useState } from "react";
import { cn } from "~/lib/utils";
import type { DriverEvent } from "~/server/api/routers/events";

/**
 * An event's artwork, with the three-step fallback the mobile cards use: the
 * event's own image, then its category's, then a glyph.
 *
 * A plain `<img>` rather than `next/image` on purpose — these URLs are absolute
 * CDN links resolved by the backend, and no `images.remotePatterns` entry
 * covers them, so `next/image` would throw at runtime for exactly the rows that
 * have artwork. A broken URL falls through to the glyph rather than showing the
 * browser's torn-image icon, which is what `onError` is for.
 *
 * Built from `<span>`s, not `<div>`s: every caller renders this inside a
 * `<button>`, whose content model is phrasing content only.
 */
export function EventThumbnail(t0) {
  const $ = _c(15);
  const {
    event,
    className,
    showCategory: t1
  } = t0;
  const showCategory = t1 === undefined ? false : t1;
  const [failed, setFailed] = useState(false);
  let t2;
  if ($[0] !== event.category?.imageUrl || $[1] !== event.imageUrl) {
    t2 = event.imageUrl?.trim() || event.category?.imageUrl?.trim() || null;
    $[0] = event.category?.imageUrl;
    $[1] = event.imageUrl;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const src = t2;
  const usable = src !== null && !failed;
  let t3;
  if ($[3] !== className) {
    t3 = cn("relative block flex-none overflow-hidden bg-(--blue-xpale)", className);
    $[3] = className;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== src || $[6] !== usable) {
    t4 = usable ? <img src={src} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" onError={() => setFailed(true)} /> : <span className="flex h-full w-full items-center justify-center"><CalendarDays className="h-5 w-5 text-(--blue-light)" /></span>;
    $[5] = src;
    $[6] = usable;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== event.category || $[9] !== showCategory) {
    t5 = showCategory && event.category && <span className="absolute bottom-1.5 left-1.5 rounded-(--r-sm) bg-black/60 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.09em] text-white">{event.category.name}</span>;
    $[8] = event.category;
    $[9] = showCategory;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t4 || $[13] !== t5) {
    t6 = <span className={t3}>{t4}{t5}</span>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}